'use client';

// Voice → prompt affordance (#82, PORTAL first). A small mic button that records
// the parent's voice (reusing useAudioRecorder), uploads it to POST
// /api/transcriptions, polls for the text, and hands it back via onText — the
// caller drops it into an editable prompt/idea field (screened downstream at
// generation, never bypassed).
//
// Multi-language: the app locale is sent as the STT language hint. `fake` provider
// in dev returns deterministic text, so this works without spend.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale } from 'next-intl';
import { Mic, Square, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils/cn';
import { useAudioRecorder } from '@/contexts/media/hooks/useAudioRecorder';
import { MicLevelMeter } from '@/contexts/media/components/MicLevelMeter';

type Phase = 'idle' | 'recording' | 'transcribing' | 'error';

export interface VoiceToPromptButtonProps {
  /** Receives the transcribed text once ready (caller merges into the field). */
  onText: (text: string) => void;
  /** Disable the control (e.g. while generation is in flight). */
  disabled?: boolean;
  className?: string;
  /** Max recording length before auto-stop (seconds). */
  maxDurationSeconds?: number;
  /** Approx token cost shown in the pre-record confirm (STT + any extract the
   *  caller runs on the result). */
  estimatedCost?: number;
}

const LABELS: Record<string, { record: string; stop: string; working: string; failed: string }> = {
  // "Dictate" — NOT "record voice": this turns speech into the text field, it
  // does not capture a voice for cloning (that lives in the voice settings).
  en: { record: 'Dictate text', stop: 'Stop dictating', working: 'Transcribing…', failed: 'Dictation failed' },
  ru: { record: 'Надиктовать текст', stop: 'Стоп', working: 'Расшифровка…', failed: 'Не удалось расшифровать' },
  uk: { record: 'Надиктувати текст', stop: 'Стоп', working: 'Розшифровка…', failed: 'Не вдалося розшифрувати' },
  cs: { record: 'Nadiktovat text', stop: 'Stop', working: 'Přepisuji…', failed: 'Diktování se nezdařilo' },
  de: { record: 'Text diktieren', stop: 'Stopp', working: 'Transkribiere…', failed: 'Diktat fehlgeschlagen' },
  fr: { record: 'Dicter le texte', stop: 'Stop', working: 'Transcription…', failed: 'Échec de la dictée' },
};

const POLL_MS = 1000;
const POLL_TIMEOUT_MS = 90_000;

/** Read a Blob as raw base64 (no data: prefix) — the worker's `audioBase64`. */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.onloadend = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

export function VoiceToPromptButton({
  onText,
  disabled,
  className,
  maxDurationSeconds = 120,
}: VoiceToPromptButtonProps) {
  const locale = useLocale();
  const labels = LABELS[locale] ?? LABELS.en;
  const recorder = useAudioRecorder({ maxDurationSeconds });
  const [phase, setPhase] = useState<Phase>('idle');
  // Guard so the audioBlob effect transcribes exactly once per recording.
  const handledBlobRef = useRef<Blob | null>(null);

  const transcribe = useCallback(
    async (blob: Blob, mimeType: string, durationSeconds: number) => {
      setPhase('transcribing');
      try {
        const audioBase64 = await blobToBase64(blob);
        const startRes = await fetch('/api/transcriptions', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ audioBase64, mimeType, language: locale, durationSeconds }),
        });
        if (!startRes.ok) throw new Error(`start ${startRes.status}`);
        const { id } = (await startRes.json()) as { id: string };

        // pushed (transcription.updated), with a fallback only while the stream is down
        const { waitForTranscript } = await import('@/contexts/ai/services/waitForTranscript');
        const rec = await waitForTranscript(id, { timeoutMs: POLL_TIMEOUT_MS, fallbackMs: POLL_MS });
        if (rec.status === 'error') throw new Error('transcription failed');
        onText((rec.text ?? '').trim());
        setPhase('idle');
        recorder.resetRecording();
      } catch {
        setPhase('error');
        recorder.resetRecording();
      }
    },
    [locale, onText, recorder],
  );

  // On stop, the recorder exposes the finished blob — transcribe it once.
  useEffect(() => {
    if (recorder.isRecording) return;
    const blob = recorder.audioBlob;
    if (!blob || handledBlobRef.current === blob) return;
    handledBlobRef.current = blob;
    void transcribe(blob, recorder.mimeType || 'audio/webm', recorder.duration);
  }, [recorder.isRecording, recorder.audioBlob, recorder.mimeType, recorder.duration, transcribe]);

  // Reflect recorder start/permission errors.
  useEffect(() => {
    if (recorder.isRecording) setPhase('recording');
  }, [recorder.isRecording]);
  useEffect(() => {
    if (recorder.error) setPhase('error');
  }, [recorder.error]);

  const beginRecording = useCallback(() => {
    handledBlobRef.current = null;
    setPhase('recording');
    void recorder.startRecording();
  }, [recorder]);

  const onClick = useCallback(() => {
    if (phase === 'recording') {
      recorder.stopRecording();
      return;
    }
    if (phase === 'transcribing') return;
    // Seamless — the server enforces the allowance.
    beginRecording();
  }, [phase, recorder, beginRecording]);

  const busy = phase === 'transcribing';
  const recording = phase === 'recording';
  const label = busy ? labels.working : recording ? labels.stop : phase === 'error' ? labels.failed : labels.record;

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={recording ? 'destructive' : 'outline'}
        onClick={onClick}
        disabled={disabled || busy}
        aria-label={label}
        title={label}
        data-testid="voice-to-prompt-button"
        data-phase={phase}
        className={cn('h-8 gap-1.5 rounded-full text-xs', className)}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" data-testid="icon-loader" />
        ) : recording ? (
          <Square className="h-3.5 w-3.5" data-testid="icon-square" />
        ) : phase === 'error' ? (
          <AlertCircle className="h-3.5 w-3.5" data-testid="icon-alert" />
        ) : (
          <Mic className="h-3.5 w-3.5" data-testid="icon-mic" />
        )}
        {recording ? (
          <>
            <MicLevelMeter variant="compact" level={recorder.level} elapsedSeconds={recorder.duration} />
            {formatDuration(recorder.duration)}
          </>
        ) : label}
      </Button>
    </>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
